import { FC, useState, useCallback, useEffect, useMemo, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  Stack,
  IconButton,
  Button,
  Switch,
  FormControlLabel,
  useTheme
} from '@mui/material';
import { Add, Remove, OpenInNew, Edit, Check, Close, Settings } from '@mui/icons-material';
import { DEFAULT_HISTORY_LEGEND_LABEL, DEFAULT_FUND_PARTNER_LABELS, DEFAULT_IMPORTANT_NOTES, SECTION_HEADER_DEFAULTS, SectionHeaders } from '../PdfPreview';
import { TableConfigurationDialog, TableData, LegacyTableData } from '../../../widgets';
import moment from 'moment';
import { getNextPublishingDate as computeNextPublishingDate, isPublishingDateOverdue } from '../../../../../../utils/automationSchedule';
import { AppContext } from '../../../../../../context';
import { CellConfigDialog } from '../../../../../../components/Dialogs/CellConfig';
import { configWrapperStyles, configFieldStyles } from '../../../widgets/TableConfigurationDialog/styles';
import { NumberFormatLocaleField } from '../../../widgets/NumberFormatLocaleField';
import { NumberFormatLocale } from '../../../../../../utils/numberFormat';

interface PdfConfigProps {
  data: any;
  onChange: (data: any) => void;
  /** Optional: parent registers flush for pending debounced edits (template 698422… implements) */
  onRegisterFlush?: (flush: () => void) => void;
  isAutomated?: boolean;
  lastPublishedAt?: string | null;
  automationFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
}

export const PdfConfig: FC<PdfConfigProps> = ({ data, onChange, isAutomated = false, lastPublishedAt = null, automationFrequency = 'daily' }) => {
  const { document } = useContext(AppContext);
  const theme = useTheme();
  const [formData, setFormData] = useState(data || {});
  const [editingHeader, setEditingHeader] = useState<{ key: keyof SectionHeaders; value: string } | null>(null);
  const [performanceDialogOpen, setPerformanceDialogOpen] = useState(false);
  const [openCellConfigDialog, setOpenCellConfigDialog] = useState<{ section: string; field: string } | null>(null);
  
  // Get ISIN from document template
  const isin = document?.template?.isins?.[0] || formData?.historyIsin || '';

  const sectionHeaders = useMemo<SectionHeaders>(() => ({
    ...SECTION_HEADER_DEFAULTS,
    ...(formData.sectionHeaders || {}),
  }), [formData.sectionHeaders]);
  const fundPartnerLabels = useMemo(() => ({
    ...DEFAULT_FUND_PARTNER_LABELS,
    ...(formData.fundPartnerLabels || {}),
  }), [formData.fundPartnerLabels]);

  useEffect(() => {
    setFormData(data || {});
  }, [data]);

  function toDateInputValue(value?: string) {
    if (!value) return '';
    // If already YYYY-MM-DD, keep as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const iso = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
    return iso.slice(0, 10);
  }

  // Calculate next publishing date based on automation frequency
  const getNextPublishingDate = (): string => {
    if (!isAutomated) return '';
    const baseDate = lastPublishedAt ? moment(lastPublishedAt) : moment();
    if (!baseDate.isValid()) return '';
    return computeNextPublishingDate(baseDate, automationFrequency).format('YYYY-MM-DD');
  };

  const nextPublishingDate = getNextPublishingDate();

  const handleFieldChange = useCallback((field: string, value: any) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  const handleObjectFieldChange = useCallback((objectKey: string, field: string, value: any) => {
    const updatedData = {
      ...formData,
      [objectKey]: {
        ...formData[objectKey],
        [field]: value
      }
    };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  const handleAddInvestmentApproach = useCallback(() => {
    const updatedPoints = [...(formData.investmentApproach || []), ''];
    const updatedData = { ...formData, investmentApproach: updatedPoints };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  const handleRemoveInvestmentApproach = useCallback((index: number) => {
    const base = [...(formData.investmentApproach || [
      'Wir investieren weltweit in börsennotierten Aktien aus nahezu allen Sektoren',
      'Wir suchen Unternehmen mit dauerhaften Wettbewerbsvorteilen',
      'Wir kaufen, wenn der Preis im attraktiven Verhältnis zum Wert steht',
      'Wir investieren mit einem langfristigen Horizont',
      'Wir denken unabhängig und handeln oft gegen den Konsens',
      'Wir investieren konzentriert in rund 10 Unternehmen',
      'Wir pflegen einen konstruktiven Dialog mit dem Management',
      'Wir informieren unsere Anleger regelmäßig und transparent'
    ])];
    base.splice(index, 1);
    const updatedData = { ...formData, investmentApproach: base };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  const handleAddTop10Stock = useCallback(() => {
    const base = [...(formData.top10Stocks || [])];
    if (base.length >= 10) return;
    base.push('');
    const updatedData = { ...formData, top10Stocks: base };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  const handleRemoveTop10Stock = useCallback((index: number) => {
    const base = [...(formData.top10Stocks || [
      'Alphabet Inc.', 'Berkshire Hathaway', 'Deutsche Post AG', 'Johnson & Johnson',
      'Microsoft Corp.', 'Nestlé SA', 'Novo Nordisk A/S', 'SAP SE',
      'UBS Group AG', 'Visa Inc.'
    ])];
    base.splice(index, 1);
    const updatedData = { ...formData, top10Stocks: base };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  const handleAddAllocItem = useCallback((key: 'countryAllocation' | 'sectorAllocation') => {
    const base = [...(formData[key] || [])];
    base.push({ label: '', value: 0 });
    const updatedData = { ...formData, [key]: base };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  const handleRemoveAllocItem = useCallback((key: 'countryAllocation' | 'sectorAllocation', index: number) => {
    const base = [...(formData[key] || [])];
    base.splice(index, 1);
    const updatedData = { ...formData, [key]: base };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  // Performance table handlers - convert to new V1-style format for dialog
  const getPerformanceData = useCallback(() => {
    const perf = formData.performance;
    // New V1-style structure (labels/values/columns)
    if (perf && perf.labels && Array.isArray(perf.labels) && perf.values && Array.isArray(perf.values)) {
      return perf;
    }
    // Legacy structure (headers/rows) - convert to new format
    if (perf && perf.headers && perf.rows) {
      return {
        labels: perf.headers,
        values: perf.rows.map((row: any) => row.values || []),
        columns: perf.columns || new Array(perf.headers.length).fill({}).map(() => ({ type: 'text' as const })),
      };
    }
    // Old key-value structure - migrate on the fly
    if (perf && typeof perf === 'object' && !perf.headers && !perf.labels) {
      return {
        labels: ['YTD', '1 y', '3 y', '5 y', '10 y', 'since issue'],
        values: [[
          perf.ytd || '8,42%',
          perf['1j'] || '6,28%',
          perf['3j'] || '18,45%',
          perf['5j'] || '25,34%',
          perf['10j'] || '89,15%',
          perf.auflage || '165,0%'
        ]],
        columns: new Array(6).fill({}).map(() => ({ type: 'text' as const })),
      };
    }
    // Default structure
    return {
      labels: ['YTD', '1 y', '3 y', '5 y', '10 y', 'since issue'],
      values: [['8,42%', '6,28%', '18,45%', '25,34%', '89,15%', '165,0%']],
      columns: new Array(6).fill({}).map(() => ({ type: 'text' as const })),
    };
  }, [formData.performance]);


  // Dialog handlers
  const handleOpenPerformanceDialog = useCallback(() => {
    setPerformanceDialogOpen(true);
  }, []);

  const handleClosePerformanceDialog = useCallback(() => {
    setPerformanceDialogOpen(false);
  }, []);

  const handleSavePerformanceDialog = useCallback((data: TableData | LegacyTableData) => {
    // Always save in new format (labels/values/columns)
    const newFormatData = 'labels' in data ? data : {
      labels: data.headers,
      values: data.rows.map(row => row.values),
      columns: new Array(data.headers.length).fill({}).map(() => ({ type: 'text' as const })),
    };
    handleFieldChange('performance', newFormatData);
  }, [handleFieldChange]);

  const startSectionHeaderEdit = useCallback((key: keyof SectionHeaders) => {
    setEditingHeader({ key, value: sectionHeaders[key] });
  }, [sectionHeaders]);

  const handleSectionHeaderInputChange = useCallback((value: string) => {
    setEditingHeader((prev) => (prev ? { ...prev, value } : prev));
  }, []);

  const handleSectionHeaderChange = useCallback((key: keyof SectionHeaders, value: string) => {
    const updatedHeaders: SectionHeaders = {
      ...sectionHeaders,
      [key]: value,
    };
    const updatedData = {
      ...formData,
      sectionHeaders: updatedHeaders,
    };
    setFormData(updatedData);
    onChange(updatedData);
    setEditingHeader(null);
  }, [formData, onChange, sectionHeaders]);

  const handleConfirmSectionHeaderEdit = useCallback(() => {
    if (!editingHeader) return;
    handleSectionHeaderChange(editingHeader.key, editingHeader.value);
  }, [editingHeader, handleSectionHeaderChange]);

  const handleCancelSectionHeaderEdit = useCallback(() => {
    setEditingHeader(null);
  }, []);

  const handleFundPartnerLabelChange = useCallback((key: keyof typeof DEFAULT_FUND_PARTNER_LABELS, value: string) => {
    const updatedLabels = {
      ...fundPartnerLabels,
      [key]: value,
    };
    const updatedData = {
      ...formData,
      fundPartnerLabels: updatedLabels,
    };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange, fundPartnerLabels]);

  const handleScrollTo = (sectionId: string) => {
    const element = window.document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderSectionHeaderControls = (
    key: keyof SectionHeaders,
    options: { variant?: 'subtitle1' | 'subtitle2'; sectionId?: string; fontWeight?: number | string } = {},
  ) => {
    const { variant = 'subtitle1', sectionId, fontWeight = 'bold' } = options;
    const isEditing = editingHeader?.key === key;

    return (
      <Box display="flex" alignItems="center" gap={2}>
        {isEditing ? (
          <>
            <TextField
              value={editingHeader?.value || ''}
              onChange={(e) => handleSectionHeaderInputChange(e.target.value)}
              size="small"
              autoFocus
              InputProps={{ sx: { fontWeight } }}
            />
            <Check
              fontSize="small"
              color="secondary"
              onClick={handleConfirmSectionHeaderEdit}
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
            <Close
              fontSize="small"
              color="secondary"
              onClick={handleCancelSectionHeaderEdit}
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
          </>
        ) : (
          <>
            <Typography variant={variant} fontWeight={fontWeight}>
              {sectionHeaders[key]}
            </Typography>
            <Edit
              fontSize="small"
              color="secondary"
              onClick={() => startSectionHeaderEdit(key)}
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
          </>
        )}
        {sectionId ? (
          <OpenInNew
            onClick={() => handleScrollTo(sectionId)}
            color="secondary"
            sx={{
              width: 20,
              height: 20,
              cursor: 'pointer',
              transition: 'color 120ms ease',
              '&:hover': { color: 'secondary.dark' },
            }}
          />
        ) : null}
      </Box>
    );
  };

  // Helper function to check if a value is dynamic
  const isValueDynamic = useCallback((section: string, field: string): boolean => {
    const value = formData[section]?.[field];
    return typeof value === 'object' && value?.isDynamic === true;
  }, [formData]);

  // Helper function to get the display value (text or config badges)
  const getValueDisplay = useCallback((section: string, field: string): string => {
    const value = formData[section]?.[field];
    if (typeof value === 'object' && value?.isDynamic) {
      return ''; // Dynamic values show config badges, not text
    }
    return typeof value === 'string' ? value : '';
  }, [formData]);

  // Helper function to toggle dynamic mode
  const handleToggleDynamic = useCallback((section: string, field: string) => {
    const currentValue = formData[section]?.[field];
    const isCurrentlyDynamic = typeof currentValue === 'object' && currentValue?.isDynamic === true;
    
    const updatedData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: isCurrentlyDynamic
          ? (typeof currentValue === 'string' ? currentValue : '') // Convert back to string
          : {
              isDynamic: true,
              fields: [],
            },
      },
    };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  // Helper function to handle cell config dialog
  const handleOpenCellConfig = useCallback((section: string, field: string) => {
    setOpenCellConfigDialog({ section, field });
  }, []);

  const handleCloseCellConfig = useCallback(() => {
    setOpenCellConfigDialog(null);
  }, []);

  const handleSetCellConfig = useCallback((config: any) => {
    if (!openCellConfigDialog) return;
    
    const { section, field } = openCellConfigDialog;
    const updatedData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: config,
      },
    };
    setFormData(updatedData);
    onChange(updatedData);
    setOpenCellConfigDialog(null);
  }, [formData, onChange, openCellConfigDialog]);

  // Helper function to render dynamic value field
  const renderDynamicValueField = useCallback((
    section: string,
    field: string,
    label: string,
    defaultValue: string
  ) => {
    const isDynamic = isValueDynamic(section, field);
    const value = getValueDisplay(section, field);
    const config = formData[section]?.[field];

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          {isDynamic ? (
            <Box sx={configWrapperStyles}>
              {config?.fields?.map((fieldConfig: any, index: number) => (
                <Box
                  key={index}
                  sx={configFieldStyles(theme, fieldConfig.type)}
                >
                  <Typography variant="subtitle2">
                    {fieldConfig.type === 'text'
                      ? fieldConfig.text
                      : `${fieldConfig.table || '(table)'}.${fieldConfig.field || '(field)'}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <TextField
              label={label}
              value={value || defaultValue}
              onChange={(e) => handleObjectFieldChange(section, field, e.target.value)}
              fullWidth
              size="small"
            />
          )}
        </Box>
        {isDynamic && (
          <IconButton
            size="small"
            onClick={() => handleOpenCellConfig(section, field)}
            sx={{ width: 32, height: 32 }}
          >
            <Settings fontSize="small" />
          </IconButton>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={isDynamic}
              onChange={() => handleToggleDynamic(section, field)}
              color="secondary"
              size="small"
            />
          }
          label="Dynamic"
          sx={{ m: 0, minWidth: 80 }}
        />
      </Box>
    );
  }, [formData, isValueDynamic, getValueDisplay, handleToggleDynamic, handleOpenCellConfig, handleObjectFieldChange]);

  return (
    <Box sx={{ p: 2, my: 64, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography variant="h6" gutterBottom>
        PDF Configuration
      </Typography>

      {/* Header Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4}>
            <Typography variant="subtitle1" fontWeight="bold">Header</Typography>
            <OpenInNew onClick={() => handleScrollTo('header-section')} color="secondary" sx={{ width: 20, height: 20, cursor: 'pointer' }} />
          </Box>
          <Stack spacing={4}>
            <TextField
              label="Title"
              value={formData.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Subtitle"
              value={formData.subtitle || ''}
              onChange={(e) => handleFieldChange('subtitle', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={isAutomated ? "Last Published At" : "Published At"}
              type="date"
              value={isAutomated && lastPublishedAt ? toDateInputValue(lastPublishedAt) : toDateInputValue(formData.published_at)}
              onChange={(e) => handleFieldChange('published_at', e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              disabled={isAutomated}
              helperText={isAutomated ? "Automated documents are published automatically by the system" : undefined}
            />
            {isAutomated && formData.data_as_of && (
              <Typography variant="body2" color="text.secondary">
                Data as of {moment(formData.data_as_of, 'YYYY-MM-DD').format('DD.MM.YYYY')}
              </Typography>
            )}
            {isAutomated && nextPublishingDate && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Next publication at {moment(nextPublishingDate).format('DD.MM.YYYY')}
                {isPublishingDateOverdue(nextPublishingDate) ? ' (overdue)' : ''}
              </Typography>
            )}
            <NumberFormatLocaleField
              value={(formData.numberFormatLocale as NumberFormatLocale) || 'DE'}
              onChange={(value) => handleFieldChange('numberFormatLocale', value)}
            />
          </Stack>
        </CardContent>
      </Card>
      {/* Investment Approach Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {renderSectionHeaderControls('investmentApproach', { sectionId: 'investment-approach-section' })}
            <IconButton size="small" onClick={handleAddInvestmentApproach} aria-label="Add investment approach item">
              <Add fontSize="small" />
            </IconButton>
          </Box>
          <Stack spacing={8}>
            {(formData.investmentApproach || []).map((item: string, index: number) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label={`Point ${index + 1}`}
                  value={item}
                  onChange={(e) => {
                    const updatedPoints = [...(formData.investmentApproach || [])];
                    updatedPoints[index] = e.target.value;
                    handleFieldChange('investmentApproach', updatedPoints);
                  }}
                  fullWidth
                  multiline
                  size="small"
                />
                <IconButton size="small" onClick={() => handleRemoveInvestmentApproach(index)} aria-label={`Remove point ${index + 1}`}>
                  <Remove fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Top 10 Stocks Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {renderSectionHeaderControls('top10Stocks', { sectionId: 'top10-stocks-section' })}
            <IconButton size="small" onClick={handleAddTop10Stock} aria-label="Add top 10 stock" disabled={(formData.top10Stocks || []).length >= 10}>
              <Add fontSize="small" />
            </IconButton>
          </Box>
          <Grid container columns={12} columnSpacing={16} rowSpacing={8}>
            {(formData.top10Stocks || []).map((stock: string, index: number) => (
              <Grid item xs={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label={`Stock ${index + 1}`}
                    value={stock}
                    onChange={(e) => {
                      const updatedStocks = [...(formData.top10Stocks || [])];
                      updatedStocks[index] = e.target.value;
                      handleFieldChange('top10Stocks', updatedStocks);
                    }}
                    fullWidth
                    size="small"
                  />
                  <IconButton size="small" onClick={() => handleRemoveTop10Stock(index)} aria-label={`Remove stock ${index + 1}`}>
                    <Remove fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Prices Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('prices', { sectionId: 'prices-section' })}
          </Box>
          <Stack spacing={2}>
            {renderDynamicValueField('prices', 'nav', 'Net Asset Value', '1.419,31 EUR')}
            {renderDynamicValueField('prices', 'fundVolume', 'Fund volume', '507.296.946 EUR')}
          </Stack>
        </CardContent>
      </Card>

      {/* Conditions Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('conditions', { sectionId: 'conditions-section' })}
          </Box>
          <Stack spacing={2}>
            {renderDynamicValueField('conditions', 'issueSurcharge', 'Front-end load', '0%')}
            {renderDynamicValueField('conditions', 'redemptionFee', 'Redemption fee', '0%')}
            {renderDynamicValueField('conditions', 'redemptionPeriod', 'Redemption notice period', '40 Tage zum Quartalsende')}
            {renderDynamicValueField('conditions', 'ongoingCosts', 'Ongoing charges (TER)', '1,09%')}
            {renderDynamicValueField('conditions', 'managementFee', 'of which management fee', '1,00%')}
            {renderDynamicValueField('conditions', 'performanceFee', 'Performance fee', '10%')}
            {renderDynamicValueField('conditions', 'hurdleRate', 'Hurdle Rate', '6% p.a.')}
            {renderDynamicValueField('conditions', 'highwaterMark', 'Highwater Mark', 'Ja')}
            {renderDynamicValueField('conditions', 'minSubscription', 'Minimum investment', '200.000 EUR')}
            {renderDynamicValueField('conditions', 'incomeUse', 'Distribution policy', 'Thesaurierend')}
          </Stack>
        </CardContent>
      </Card>

      {/* Master Data Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('masterData', { sectionId: 'master-data-section' })}
          </Box>
          <Stack spacing={2}>
            {renderDynamicValueField('masterData', 'isinWkn', 'ISIN / WKN', 'DE000A0RAAR6 / A0RAAR')}
            {renderDynamicValueField('masterData', 'fundCurrency', 'Fund currency', 'EUR')}
            {renderDynamicValueField('masterData', 'launchDate', 'Inception date', '30.09.2008')}
            {renderDynamicValueField('masterData', 'initialPrice', 'Initial issue price', '100 EUR')}
            {renderDynamicValueField('masterData', 'fundDomicile', 'Fund domicile', 'Deutschland')}
          </Stack>
        </CardContent>
      </Card>

      {/* Performance Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {renderSectionHeaderControls('performance', { sectionId: 'performance-section' })}
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} width="100%">
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={handleOpenPerformanceDialog}
                size="small"
              >
                Configure Table
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Performance Table Configuration Dialog */}
      <TableConfigurationDialog
        open={performanceDialogOpen}
        onClose={handleClosePerformanceDialog}
        onSave={handleSavePerformanceDialog}
        initialData={getPerformanceData()}
        title="Configure Performance Table"
        isin={isin}
      />

      {/* Wertentwicklung seit Auflage (Chart) Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('chart', { sectionId: 'chart-section' })}
          </Box>
          <Grid container spacing={2} rowSpacing={8} columnSpacing={8} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                label="ISIN (override)"
                placeholder='e.g. DE000A0RAAR6'
                value={formData.historyIsin || ''}
                onChange={(e) => handleFieldChange('historyIsin', e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End date (until)"
                type="date"
                value={toDateInputValue(formData.historyUntil)}
                onChange={(e) => handleFieldChange('historyUntil', e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Legend label"
                value={formData.historyLegendLabel ?? ''}
                onChange={(e) => handleFieldChange('historyLegendLabel', e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Allocation Sections */}
      {/* <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {renderSectionHeaderControls('countryAllocation', { sectionId: 'pie-section' })}
            <IconButton size="small" onClick={() => handleAddAllocItem('countryAllocation')} aria-label="Add country allocation">
              <Add fontSize="small" />
            </IconButton>
          </Box>
          <Grid container columns={12} columnSpacing={16} rowSpacing={8}>
            {(formData.countryAllocation || []).map((item: any, index: number) => (
              <Grid item xs={12} key={`ca-${index}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label={`Label ${index + 1}`}
                    value={item.label || ''}
                    onChange={(e) => {
                      const list = [...(formData.countryAllocation || [])];
                      list[index] = { ...list[index], label: e.target.value };
                      handleFieldChange('countryAllocation', list);
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Value %"
                    type="number"
                    value={item.value ?? 0}
                    onChange={(e) => {
                      const list = [...(formData.countryAllocation || [])];
                      list[index] = { ...list[index], value: Number(e.target.value) };
                      handleFieldChange('countryAllocation', list);
                    }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                  <IconButton size="small" onClick={() => handleRemoveAllocItem('countryAllocation', index)} aria-label={`Remove country ${index + 1}`}>
                    <Remove fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {renderSectionHeaderControls('sectorAllocation', { sectionId: 'sector-allocation-section' })}
            <IconButton size="small" onClick={() => handleAddAllocItem('sectorAllocation')} aria-label="Add sector allocation">
              <Add fontSize="small" />
            </IconButton>
          </Box>
          <Grid container columns={12} columnSpacing={16} rowSpacing={8}>
            {(formData.sectorAllocation || []).map((item: any, index: number) => (
              <Grid item xs={12} key={`sa-${index}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label={`Label ${index + 1}`}
                    value={item.label || ''}
                    onChange={(e) => {
                      const list = [...(formData.sectorAllocation || [])];
                      list[index] = { ...list[index], label: e.target.value };
                      handleFieldChange('sectorAllocation', list);
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Value %"
                    type="number"
                    value={item.value ?? 0}
                    onChange={(e) => {
                      const list = [...(formData.sectorAllocation || [])];
                      list[index] = { ...list[index], value: Number(e.target.value) };
                      handleFieldChange('sectorAllocation', list);
                    }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                  <IconButton size="small" onClick={() => handleRemoveAllocItem('sectorAllocation', index)} aria-label={`Remove sector ${index + 1}`}>
                    <Remove fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card> */}

      {/* Fund Partner Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            {renderSectionHeaderControls('fundPartner', { sectionId: 'fund-partner-section', variant: 'subtitle2', fontWeight: 600 })}
          </Box>
          <Stack spacing={2}>
            <Box display="flex" gap={8}>
              <TextField
                label="Label"
                value={fundPartnerLabels.custodian}
                onChange={(e) => handleFundPartnerLabelChange('custodian', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Verwahrstelle"
                value={formData.fundPartner?.custodian || ''}
                onChange={(e) => handleFieldChange('fundPartner', { ...formData.fundPartner, custodian: e.target.value })}
                fullWidth
                size="small"
              />
            </Box>
            <Box display="flex" gap={8}>
              <TextField
                label="Label"
                value={fundPartnerLabels.capitalManagement}
                onChange={(e) => handleFundPartnerLabelChange('capitalManagement', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Kapitalverwaltungsgesellschaft"
                value={formData.fundPartner?.capitalManagement || ''}
                onChange={(e) => handleFieldChange('fundPartner', { ...formData.fundPartner, capitalManagement: e.target.value })}
                fullWidth
                size="small"
              />
            </Box>
            <Box display="flex" gap={8}>
              <TextField
                label="Label"
                value={fundPartnerLabels.fundAdvisor}
                onChange={(e) => handleFundPartnerLabelChange('fundAdvisor', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Fondsberater"
                value={formData.fundPartner?.fundAdvisor || ''}
                onChange={(e) => handleFieldChange('fundPartner', { ...formData.fundPartner, fundAdvisor: e.target.value })}
                fullWidth
                size="small"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Contact Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            {renderSectionHeaderControls('contact', { sectionId: 'contact-section', variant: 'subtitle2', fontWeight: 600 })}
          </Box>
          <Stack spacing={2}>
            <TextField
              label="Company Name"
              value={formData.contact?.companyName || ''}
              onChange={(e) => handleFieldChange('contact', { ...formData.contact, companyName: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Address"
              value={formData.contact?.address || ''}
              onChange={(e) => handleFieldChange('contact', { ...formData.contact, address: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              value={formData.contact?.email || ''}
              onChange={(e) => handleFieldChange('contact', { ...formData.contact, email: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Website"
              value={formData.contact?.website || ''}
              onChange={(e) => handleFieldChange('contact', { ...formData.contact, website: e.target.value })}
              fullWidth
              size="small"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Important Notes Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('importantNotes', { sectionId: 'important-notes-section' })}
          </Box>
          <TextField
            label="Important Notes"
            value={formData.importantNotes || DEFAULT_IMPORTANT_NOTES}
            onChange={(e) => handleFieldChange('importantNotes', e.target.value)}
            fullWidth
            multiline
            rows={15}
            size="small"
            placeholder={DEFAULT_IMPORTANT_NOTES}
          />
        </CardContent>
      </Card>

      {/* Cell Config Dialog */}
      {openCellConfigDialog && (
        <CellConfigDialog
          isin={isin}
          config={formData[openCellConfigDialog.section]?.[openCellConfigDialog.field]}
          open={!!openCellConfigDialog}
          onClose={handleCloseCellConfig}
          onConfirm={handleSetCellConfig}
        />
      )}
    </Box>
  );
};

